import { ColumnDef, sortingFns } from '@tanstack/react-table';
import React from 'react';
import { Link } from 'react-router-dom';
import { getRiskConsequencesName, getRiskEventsName, getRiskSourcesName, getScenarioStatusName } from 'src/api/types';
import { GTInternalIds } from 'src/components/GenericTable/types';
import { formatDate, numeralFormat } from 'src/utils/misc';
import GenericTable, { GTColumnSelectAnchor, GTTdm } from 'src/components/GenericTable';
import { filter } from 'lodash';
import { IncompleteStateMarkerNew, TextOverflowTooltip } from 'src/common';
import { TableIds } from 'src/api/types/misc';
import useLocalStorage from 'src/hooks/useLocalStorage';
import { TLLink } from 'src/pages/DashboardPage/comps';
import { IncidentsQuery } from 'src/graphql/_generated';

type ScenarioItem = NonNullable<IncidentsQuery['incidents'][number]['scenarios']>[number];

interface TableExpandContentProps {
  data: ScenarioItem[];
}

const defaultColumns = ['status_scen', 'name', 'source', 'event', 'consequence', 'expected_loss', 'updated_at'];
const internalIds = [GTInternalIds.expander, GTInternalIds.tdm];

export const TableExpandContent: React.FC<TableExpandContentProps> = ({ data }) => {
  const [selectedColumns, setSelectedColumns] = useLocalStorage(
    `${TableIds.incidentsScenarios}-columns`,
    defaultColumns,
  );

  const columns: ColumnDef<ScenarioItem>[] = [
    {
      id: 'assessment_name',
      header: 'Assessment',
      accessorFn: (row) => row.assessment?.name,
      cell: (info) => info.getValue(),
      sortUndefined: 'last',
      sortingFn: sortingFns.text,
    },
    {
      id: 'status_scen',
      header: 'Status',
      accessorFn: (row) => getScenarioStatusName(row.status),
      cell: (info) => info.getValue(),
    },
    {
      id: 'name',
      header: 'Scenario',
      accessorFn: (row) => row.name,
      cell: (info) => (
        <TextOverflowTooltip>
          <Link
            className="table-link"
            to={`/risk-assessments/${info.row.original.assessment?.id}/scenario/${info.row.original.id}`}
            onClick={(e) => e.stopPropagation()}
          >
            {info.getValue() as string}
          </Link>
        </TextOverflowTooltip>
      ),
      sortUndefined: 'last',
      sortingFn: sortingFns.text,
    },
    {
      id: 'source',
      header: 'Source',
      accessorFn: (row) => getRiskSourcesName(row.source),
      cell: (info) => info.getValue(),
    },
    {
      id: 'event',
      header: 'Event',
      accessorFn: (row) => getRiskEventsName(row.event),
      cell: (info) => info.getValue(),
    },
    {
      id: 'consequence',
      header: 'Consequence',
      accessorFn: (row) => getRiskConsequencesName(row.consequence),
      cell: (info) => info.getValue(),
    },
    {
      id: 'risk_owner',
      header: 'Risk Owner',
      accessorFn: (row) => row.ownerName,
      cell: (info) => info.getValue(),
      sortingFn: sortingFns.text,
    },
    {
      id: 'expected_loss',
      header: 'Expected Loss',
      accessorFn: (row) => row.liveRisk?.expectedLoss || null,
      cell: (info) => numeralFormat(info.getValue() as number),
    },
    // {
    //   id: '_control_count',
    //   header: 'Linked Controls',
    //   accessorFn: (row) => row.riskManagementPlans?.length || null,
    //   cell: (info) => info.getValue(),
    // },
    {
      id: 'risk_identified_date',
      header: 'Identified Date',
      accessorFn: (row) => row?.riskIdentifiedDate || '',
      cell: (info) => <TLLink date={info.getValue() as string}>{formatDate(info.getValue() as string)}</TLLink>,
    },
    {
      id: 'updated_at',
      header: 'Last Updated',
      accessorFn: (row) => row.meta?.updatedAt || null,
      cell: ({ row }) => {
        const date = row.original.meta?.updatedAt;
        if (!date) return null;
        return <TLLink date={date}>{formatDate(date, { formatType: 'datetime' })}</TLLink>;
      },
    },
    {
      id: 'created_at',
      header: 'Created Date',
      accessorFn: (row) => row.meta?.createdAt || null,
      cell: ({ row }) => {
        const date = row.original.meta?.createdAt;
        if (!date) return null;
        return <TLLink date={date}>{formatDate(date, { formatType: 'datetime' })}</TLLink>;
      },
    },
    {
      id: GTInternalIds.tdm,
      header: () => null,
      cell: ({ row }) => {
        return (
          <div css="display: flex;grid-gap: 8px">
            <GTTdm extraComponentLeft={<IncompleteStateMarkerNew scenario={row.original} />} />
          </div>
        );
      },
      size: 1,
    },
  ];

  return (
    <GenericTable
      tableId={TableIds.incidentsScenarios}
      data={data}
      columns={filter(columns, (el) => [...internalIds, ...selectedColumns].includes(el.id as string))}
      GTColumnSelectAnchorExported={
        <GTColumnSelectAnchor
          tableId={TableIds.incidentsScenarios}
          options={[
            { key: 'assessment_name', title: 'Assessment' },
            { key: 'status_scen', title: 'Status' },
            { key: 'name', title: 'Scenario' },
            { key: 'source', title: 'Source' },
            { key: 'event', title: 'Event' },
            { key: 'consequence', title: 'Consequence' },
            {
              key: 'risk_owner',
              title: 'Risk Owner',
            },
            {
              key: 'expected_loss',
              title: 'Expected Loss',
            },
            // {
            //   key: '_control_count',
            //   title: 'Linked Controls',
            // },
            {
              key: 'risk_identified_date',
              title: 'Identified Date',
            },
            {
              key: 'updated_at',
              title: 'Last Updated',
            },
            {
              key: 'created_at',
              title: 'Created Date',
            },
          ]}
          defaultOptions={defaultColumns}
          value={selectedColumns}
          onChange={setSelectedColumns}
        />
      }
      darkMode
    />
  );
};
